import React, {useEffect} from 'react';
import {useI18nContext} from "../../contexts/I18nContext";
import {useStyles} from "./BillingPeriodClosure.style";
import {Observer} from "mobx-react-lite";
import MessageCard from "../../ui_components/MessageCard/MessageCard";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BillingPeriodClosureRow from "./BillingPeriodClosureRow/BillingPeriodClosureRow";
import BillingPeriodClosureStore from "./BillingPeriodClosureStore";
import {useCommonContext} from "../../contexts/CommonContext";
import {useNavigate, useParams} from "react-router-dom";
import ShowDate from "../../shared_components/Info/ShowDate/ShowDate";
import ShowArticleDescription from "../../shared_components/Info/ShowArticleDescription/ShowArticleDescription";
import ShowBillingPeriodStatus from "../../shared_components/Info/ShowBillingPeriodStatus/ShowBillingPeriodStatus";
import ShowPrice from "../../shared_components/Info/ShowPrice/ShowPrice";
import Box from "../../ui_components/Box/Box";
import Width from "../../ui_components/common/enums/Width";
import Alignment from "../../ui_components/common/enums/Alignment";
import Link from '../../ui_components/Link/Link';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Checkbox from '../../ui_components/Checkbox/Checkbox';
import BillingPeriodStatus from '../../entities/BillingPeriodStatus';

const BillingPeriodClosure: React.FC = () => {
    const commonStore = useCommonContext();
    const i18n = useI18nContext();
    const { id } = useParams();
    const store = new BillingPeriodClosureStore(commonStore, id || '');
    const classes = useStyles();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            store.loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleBackOnclick = async () => {
        navigate(`/billing-periods`, { replace: true });
    }

    const onNoZeroValuesChange = (checked: boolean) => {
        store.setNoZeroValues(checked);
    }

    const onUpdate = async () => {
        await store.loadClosures();
        store.setNoZeroValues(store.noZero);
    }

    return (
        <Observer>
            {() => (
                <div className={classes.closure_container}>
                    <div>
                        <Link startIcon={ArrowBackIosIcon} text={'Regresar'} onClick={handleBackOnclick}></Link>
                    </div>
                    {store.billingPeriod && (
                        <div className={classes.period_header}>
                            <div className={classes.period_dates_row}>
                                <div className={classes.dates}>
                                    <ShowDate label={'Inicio'} timestamp={store.billingPeriod.startDate} showTime={false}/>
                                    <ShowDate label={'Fin'} timestamp={store.billingPeriod.endDate} showTime={false}/>
                                </div>
                                 <ShowBillingPeriodStatus status={store.billingPeriod.status}/>
                            </div>
                            <div className={classes.period_info_row}>
                                <ShowArticleDescription 
                                    description={store.billingPeriod.description}
                                    className={classes.period_description}
                                />
                            </div>
                            <Box className={classes.total_container} 
                                 width={Width.FULL_WIDTH}
                                 justifyContent={Alignment.END}>
                                <div className={classes.total_label}>
                                    {'TOTAL'}:
                                </div>
                                <ShowPrice value={store.totalAmount} className={classes.total_price}/>
                            </Box>
                        </div>
                    )}

                    {!store.isEmpty && (
                        <div className={classes.closures_table}>
                            <div>
                                <Checkbox text={'Ocultar valores nulos'} onChange={onNoZeroValuesChange} />
                            </div>
                            <div className={classes.table_header}>
                                <div className={classes.header_agency}>{i18n.translate("AGENCY")}</div>
                                <div className={classes.header_amount}>{i18n.translate("AMOUNT")}</div>
                                <div className={classes.header_notes}>{i18n.translate("COMMENT")}</div>
                            </div>
                            <div className={classes.elements_container}>
                                {store.billingPeriod && store.filteredClosures.map((closure, i) => (
                                    <BillingPeriodClosureRow
                                        closure={closure}
                                        billingPeriodStatus={store.billingPeriod?.status || BillingPeriodStatus.OPEN}
                                        billingPeriodStart={store.billingPeriod?.startDate ?? 0}
                                        billingPeriodEnd={store.billingPeriod?.endDate ?? 0}
                                        key={i}
                                        onUpdate={onUpdate}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {store.isEmpty && !store.isLoading && (
                        <MessageCard 
                            icon={AttachMoneyIcon} 
                            title={i18n.translate("NO_CLOSURES_TITLE")}
                            subtitle={i18n.translate("NO_CLOSURES_SUBTITLE")}
                        />
                    )}
                </div>
            )}
        </Observer>
    )
}

export default BillingPeriodClosure;
